import React, { useContext, useEffect, useState } from "react";
import { ArrowUpRight, Crown, ShieldCheck, Sparkles, Upload, Users } from "lucide-react";
import { AuthContext } from "../AuthContext";
import { API_BASE_URL } from "../api";
import { Button, Field, MetricCard, SectionHeader, StatusPill, SurfaceCard } from "../components/designSystem";

const DEFAULT_BANK_TRANSFER = {
  accountName: "MekBridge LTD.",
  bankName: "VFD Microfinance Bank",
  accountNumber: "1041533406",
  note: "Use your account email or a clear transfer reference, then submit the form below so an admin can review and activate your plan.",
};

const DEFAULT_PRO_PRICING = {
  "1m": { amount: 10, currency: "NGN", label: "₦10k" },
  "3m": { amount: 27, currency: "NGN", label: "₦27k" },
  "6m": { amount: 50, currency: "NGN", label: "₦50k" },
  "12m": { amount: 90, currency: "NGN", label: "₦90k" },
};

const durationOptions = [
  { value: "1m", label: "1 month" },
  { value: "3m", label: "3 months" },
  { value: "6m", label: "6 months" },
  { value: "12m", label: "12 months" },
];

const statusTone = {
  pending: "warning",
  approved: "success",
  rejected: "danger",
  cancelled: "neutral",
};

function formatStatusLabel(status) {
  if (!status) {
    return "No request";
  }

  return status.charAt(0).toUpperCase() + status.slice(1);
}

function formatDate(value) {
  if (!value) {
    return "-";
  }

  return new Date(value).toLocaleString();
}

async function readApiResponse(response) {
  const contentType = response.headers.get("content-type") || "";
  const rawText = await response.text();

  if (!rawText) {
    return null;
  }

  if (contentType.includes("application/json")) {
    try {
      return JSON.parse(rawText);
    } catch {
      throw new Error("The server returned invalid JSON for the upgrade endpoint.");
    }
  }

  if (rawText.trim().startsWith("<!DOCTYPE") || rawText.trim().startsWith("<html")) {
    throw new Error("The upgrade endpoint returned HTML instead of JSON. Check that the frontend is pointing to the backend API and that the backend route is available.");
  }

  try {
    return JSON.parse(rawText);
  } catch {
    throw new Error(rawText);
  }
}

export default function UpgradeDashboard() {
  const { token, user } = useContext(AuthContext);
  const [plan, setPlan] = useState("pro");
  const [duration, setDuration] = useState("1m");
  const [payerName, setPayerName] = useState(user?.username || "");
  const [paymentReference, setPaymentReference] = useState("");
  const [paymentProofFile, setPaymentProofFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [pageLoading, setPageLoading] = useState(true);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [copyMessage, setCopyMessage] = useState("");
  const [upgradeData, setUpgradeData] = useState({
    bankTransfer: DEFAULT_BANK_TRANSFER,
    pricing: {
      pro: DEFAULT_PRO_PRICING,
      enterprise: {
        label: "Manual review",
        description: "Enterprise pricing is approved manually after payment confirmation.",
      },
    },
    request: null,
    currentPlan: user?.plan || "free",
    proPlanEnd: null,
  });

  useEffect(() => {
    if (!token) {
      return;
    }

    async function loadUpgradeDetails() {
      setPageLoading(true);
      setError("");

      try {
        const response = await fetch(`${API_BASE_URL}/api/upgrade`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        const data = await readApiResponse(response);

        if (!response.ok) {
          throw new Error(data?.error || "Failed to load upgrade details.");
        }

        setUpgradeData({
          bankTransfer: data?.bankTransfer || DEFAULT_BANK_TRANSFER,
          pricing: data?.pricing || {
            pro: DEFAULT_PRO_PRICING,
            enterprise: {
              label: "Manual review",
              description: "Enterprise pricing is approved manually after payment confirmation.",
            },
          },
          request: data.request || null,
          currentPlan: data.currentPlan || user?.plan || "free",
          proPlanEnd: data.proPlanEnd || null,
        });

        if (data?.request?.requestedPlan) {
          setPlan(data.request.requestedPlan);
        }

        if (data?.request?.requestedDuration) {
          setDuration(data.request.requestedDuration);
        }

        if (data?.request?.payerName) {
          setPayerName(data.request.payerName);
        }

        if (data?.request?.paymentReference) {
          setPaymentReference(data.request.paymentReference);
        }
      } catch (loadError) {
        setError(loadError.message || "Failed to load upgrade details.");
      } finally {
        setPageLoading(false);
      }
    }

    loadUpgradeDetails();
  }, [token, user?.plan, user?.username]);

  useEffect(() => {
    if (!copyMessage) {
      return undefined;
    }

    const timeoutId = window.setTimeout(() => setCopyMessage(""), 2200);
    return () => window.clearTimeout(timeoutId);
  }, [copyMessage]);

  async function copyToClipboard(value, label) {
    try {
      await navigator.clipboard.writeText(value);
      setCopyMessage(`${label} copied.`);
    } catch {
      setCopyMessage(`Copy failed. ${label}: ${value}`);
    }
  }

  async function handleUpgrade() {
    setLoading(true);
    setMessage("");
    setError("");

    try {
      const formData = new FormData();
      formData.append("plan", plan);
      if (plan === "pro") {
        formData.append("duration", duration);
      }
      formData.append("payerName", payerName);
      formData.append("paymentReference", paymentReference);
      if (paymentProofFile) {
        formData.append("paymentProof", paymentProofFile);
      }

      const response = await fetch(`${API_BASE_URL}/api/upgrade`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: formData,
      });
      const data = await readApiResponse(response);

      if (!response.ok) {
        throw new Error(data?.error || "Failed to submit payment request.");
      }

      setUpgradeData((current) => ({
        ...current,
        bankTransfer: data.bankTransfer || current.bankTransfer,
        request: data.request || current.request,
      }));
      setPaymentProofFile(null);
      setMessage(data.message || "Payment request submitted.");
    } catch (upgradeError) {
      setError(upgradeError.message || "Failed to submit payment request.");
    } finally {
      setLoading(false);
    }
  }

  const request = upgradeData.request;
  const currentPlan = upgradeData.currentPlan || user?.plan || "free";
  const selectedPricing = plan === "pro" ? upgradeData.pricing?.pro?.[duration] : null;
  const requestStatus = request?.status || null;
  const requestStatusLabel = formatStatusLabel(requestStatus);
  const isAlreadyEnterprise = currentPlan === "enterprise";
  const selectedAmountLabel =
    plan === "pro"
      ? selectedPricing?.label || `${selectedPricing?.currency || "USD"} ${selectedPricing?.amount ?? "-"}`
      : upgradeData.pricing?.enterprise?.label || "Manual quote";

  return (
    <div className="mx-auto max-w-6xl space-y-6">
      <SurfaceCard className="overflow-hidden bg-[radial-gradient(circle_at_top_left,_rgba(250,204,21,0.18),_transparent_24%),radial-gradient(circle_at_80%_20%,_rgba(14,165,233,0.12),_transparent_18%),linear-gradient(135deg,_#ffffff,_#f8fafc)] dark:bg-[radial-gradient(circle_at_top_left,_rgba(250,204,21,0.14),_transparent_24%),radial-gradient(circle_at_80%_20%,_rgba(14,165,233,0.12),_transparent_18%),linear-gradient(135deg,_#111827,_#0f172a)]">
        <SectionHeader
          eyebrow="Upgrade Workspace"
          title="Submit payment, then unlock by admin review"
          description="Pay by bank transfer, submit the transfer reference here, and your entitlement will only be activated after an admin confirms the payment."
          action={<StatusPill tone={statusTone[requestStatus] || "brand"}>{request ? requestStatusLabel : "Manual approval flow"}</StatusPill>}
        />
        <div className="mt-6 grid gap-4 md:grid-cols-3">
          <MetricCard label="Current plan" value={currentPlan ? currentPlan.toUpperCase() : "FREE"} helper="Your live entitlement tier right now." />
          <MetricCard label="Payment method" value="Bank transfer" helper="No plan change happens until an admin approves the payment." />
          <MetricCard label="Request status" value={pageLoading ? "Loading..." : requestStatusLabel} helper={upgradeData.proPlanEnd ? `Current Pro access ends ${formatDate(upgradeData.proPlanEnd)}.` : "Pending requests stay queued until reviewed."} />
        </div>
      </SurfaceCard>

      {error ? (
        <SurfaceCard>
          <p className="text-sm font-medium text-rose-600 dark:text-rose-300">{error}</p>
        </SurfaceCard>
      ) : null}

      {message ? (
        <SurfaceCard>
          <p className="text-sm font-medium text-emerald-700 dark:text-emerald-300">{message}</p>
        </SurfaceCard>
      ) : null}

      {copyMessage ? (
        <SurfaceCard>
          <p className="text-sm font-medium text-sky-700 dark:text-sky-300">{copyMessage}</p>
        </SurfaceCard>
      ) : null}

      <div className="grid gap-6 xl:grid-cols-[1.05fr_0.95fr]">
        <SurfaceCard>
          <SectionHeader
            eyebrow="Payment request"
            title="Choose plan and submit your transfer reference"
            description="This form records payment intent for admin review. It does not grant access on its own."
          />
          <div className="mt-6 grid gap-4 md:grid-cols-2">
            <button
              type="button"
              onClick={() => setPlan("pro")}
              className={`rounded-[24px] border p-5 text-left transition ${plan === "pro" ? "border-slate-900 bg-slate-900 text-white dark:border-white dark:bg-white dark:text-slate-900" : "border-slate-200 bg-slate-50 text-slate-900 dark:border-gray-700 dark:bg-gray-900 dark:text-white"}`}
            >
              <Sparkles className="h-5 w-5" />
              <p className="mt-4 text-lg font-semibold">Pro</p>
              <p className="mt-2 text-sm leading-7 opacity-80">Advanced analytics, journal depth, exports, and premium workflow tools.</p>
            </button>
            <button
              type="button"
              onClick={() => setPlan("enterprise")}
              className={`rounded-[24px] border p-5 text-left transition ${plan === "enterprise" ? "border-amber-500 bg-amber-500 text-slate-950" : "border-slate-200 bg-slate-50 text-slate-900 dark:border-gray-700 dark:bg-gray-900 dark:text-white"}`}
            >
              <Crown className="h-5 w-5" />
              <p className="mt-4 text-lg font-semibold">Enterprise</p>
              <p className="mt-2 text-sm leading-7 opacity-80">Teams, shared workspaces, and premium coordination layers for funded groups.</p>
            </button>
          </div>

          {plan === "pro" ? (
            <div className="mt-6">
              <p className="text-sm font-semibold text-slate-700 dark:text-slate-200">Duration</p>
              <div className="mt-3 flex flex-wrap gap-3">
                {durationOptions.map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => setDuration(option.value)}
                    className={`rounded-full border px-4 py-2 text-sm font-medium transition ${duration === option.value ? "border-sky-600 bg-sky-600 text-white" : "border-slate-200 bg-white text-slate-700 dark:border-gray-700 dark:bg-gray-900 dark:text-slate-200"}`}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <div className="mt-6 rounded-3xl border border-amber-200 bg-amber-50 p-4 dark:border-amber-500/20 dark:bg-amber-500/10">
              <p className="text-sm font-semibold text-amber-800 dark:text-amber-300">Enterprise is reviewed manually</p>
              <p className="mt-2 text-sm leading-7 text-amber-700 dark:text-amber-200">Submit the bank transfer reference and admin can confirm pricing, activate the plan, and open the team workspace.</p>
            </div>
          )}

          <div className="mt-6 rounded-[24px] border border-slate-200 bg-slate-50 p-5 dark:border-gray-700 dark:bg-gray-900">
            <p className="text-xs font-semibold uppercase tracking-[0.28em] text-slate-400">Amount due</p>
            <p className="mt-3 text-3xl font-semibold text-slate-950 dark:text-white">
              {selectedAmountLabel}
            </p>
            <p className="mt-2 text-sm leading-7 text-slate-600 dark:text-slate-300">
              {plan === "pro"
                ? "Transfer this amount, then submit the exact payment reference below."
                : "Enterprise payments are handled manually. Submit a clear reference so admin can match and activate your account."}
            </p>
          </div>

          <div className="mt-6 grid gap-4 md:grid-cols-2">
            <Field
              label="Payer name"
              value={payerName}
              onChange={(event) => setPayerName(event.target.value)}
              placeholder="Name used on the transfer"
            />
            <Field
              label="Transfer reference"
              value={paymentReference}
              onChange={(event) => setPaymentReference(event.target.value)}
              placeholder="Bank narration or transaction reference"
              hint="Required so admin can match your transfer quickly."
            />
          </div>

          <div className="mt-6 rounded-3xl border border-slate-200 bg-slate-50 p-5 dark:border-gray-700 dark:bg-gray-900">
            <label className="block">
              <span className="mb-2 block text-sm font-semibold text-slate-700 dark:text-slate-200">Payment proof</span>
              <input
                type="file"
                accept="image/*,.pdf"
                onChange={(event) => setPaymentProofFile(event.target.files?.[0] || null)}
                className="block w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 dark:border-gray-700 dark:bg-gray-800 dark:text-slate-100"
              />
            </label>
            <p className="mt-2 text-xs leading-6 text-slate-500 dark:text-slate-400">Upload a receipt screenshot or transfer confirmation PDF so admin can verify the payment faster.</p>
            {paymentProofFile ? (
              <div className="mt-3 inline-flex items-center gap-2 rounded-full border border-sky-200 bg-sky-50 px-3 py-1 text-xs font-semibold text-sky-700 dark:border-sky-500/20 dark:bg-sky-500/10 dark:text-sky-300">
                <Upload className="h-3.5 w-3.5" />
                {paymentProofFile.name}
              </div>
            ) : null}
          </div>

          <div className="mt-6">
            <Button type="button" variant="primary" onClick={handleUpgrade} disabled={loading || pageLoading || isAlreadyEnterprise}>
              <ArrowUpRight className="h-4 w-4" />
              {loading
                ? "Submitting request..."
                : isAlreadyEnterprise
                  ? "Already enterprise"
                  : request?.status === "pending"
                    ? `Update ${plan.toUpperCase()} request`
                    : `Submit ${plan.toUpperCase()} payment request`}
            </Button>
            <p className="mt-3 text-xs leading-6 text-slate-500 dark:text-slate-400">Admin approval is the gate for entitlement. This page now records requests instead of granting plans automatically.</p>
          </div>
        </SurfaceCard>

        <div className="space-y-6">
          <SurfaceCard>
            <SectionHeader eyebrow="Bank details" title="Transfer destination" description="Set your live bank details with backend environment variables when you are ready. Until then, these placeholders keep the workflow wired correctly." />
            <div className="mt-6 space-y-4">
              <div className="rounded-3xl border border-slate-200 bg-slate-50 p-4 dark:border-gray-700 dark:bg-gray-900">
                <p className="text-xs font-semibold uppercase tracking-[0.28em] text-slate-400">Account name</p>
                <p className="mt-2 text-lg font-semibold text-slate-950 dark:text-white">{upgradeData.bankTransfer?.accountName || "Loading..."}</p>
                <button type="button" onClick={() => copyToClipboard(upgradeData.bankTransfer?.accountName || "", "Account name")} className="mt-3 text-sm font-medium text-sky-700 transition hover:text-sky-600 dark:text-sky-300 dark:hover:text-sky-200">
                  Copy account name
                </button>
              </div>
              <div className="rounded-3xl border border-slate-200 bg-slate-50 p-4 dark:border-gray-700 dark:bg-gray-900">
                <p className="text-xs font-semibold uppercase tracking-[0.28em] text-slate-400">Bank name</p>
                <p className="mt-2 text-lg font-semibold text-slate-950 dark:text-white">{upgradeData.bankTransfer?.bankName || "Loading..."}</p>
                <button type="button" onClick={() => copyToClipboard(upgradeData.bankTransfer?.bankName || "", "Bank name")} className="mt-3 text-sm font-medium text-sky-700 transition hover:text-sky-600 dark:text-sky-300 dark:hover:text-sky-200">
                  Copy bank name
                </button>
              </div>
              <div className="rounded-3xl border border-slate-200 bg-slate-50 p-4 dark:border-gray-700 dark:bg-gray-900">
                <p className="text-xs font-semibold uppercase tracking-[0.28em] text-slate-400">Account number</p>
                <p className="mt-2 text-lg font-semibold tracking-[0.18em] text-slate-950 dark:text-white">{upgradeData.bankTransfer?.accountNumber || "Loading..."}</p>
                <button type="button" onClick={() => copyToClipboard(upgradeData.bankTransfer?.accountNumber || "", "Account number")} className="mt-3 text-sm font-medium text-sky-700 transition hover:text-sky-600 dark:text-sky-300 dark:hover:text-sky-200">
                  Copy account number
                </button>
              </div>
              <div className="rounded-3xl border border-slate-200 bg-slate-50 p-4 dark:border-gray-700 dark:bg-gray-900">
                <div className="flex items-start gap-3">
                  <ShieldCheck className="mt-1 h-5 w-5 text-emerald-600 dark:text-emerald-300" />
                  <div>
                    <p className="text-sm font-semibold text-slate-950 dark:text-white">Transfer instruction</p>
                    <p className="mt-1 text-sm leading-7 text-slate-600 dark:text-slate-300">{upgradeData.bankTransfer?.note || "Loading instructions..."}</p>
                  </div>
                </div>
              </div>
            </div>
          </SurfaceCard>

          <SurfaceCard>
            <SectionHeader eyebrow="Review state" title="Latest request" description="This is the live state of your most recent payment submission." />
            <div className="mt-6 space-y-4">
              <div className="flex items-center justify-between gap-3 rounded-3xl border border-slate-200 bg-slate-50 p-4 dark:border-gray-700 dark:bg-gray-900">
                <div className="flex items-center gap-3">
                  <Users className="h-5 w-5 text-amber-600 dark:text-amber-300" />
                  <div>
                    <p className="text-sm font-semibold text-slate-950 dark:text-white">Status</p>
                    <p className="mt-1 text-sm leading-7 text-slate-600 dark:text-slate-300">{request ? `${request.requestedPlan?.toUpperCase()} request submitted ${formatDate(request.submittedAt)}.` : "No payment request submitted yet."}</p>
                  </div>
                </div>
                <StatusPill tone={statusTone[requestStatus] || "neutral"}>{requestStatusLabel}</StatusPill>
              </div>
              {request ? (
                <div className="rounded-3xl border border-slate-200 bg-slate-50 p-4 text-sm leading-7 text-slate-600 dark:border-gray-700 dark:bg-gray-900 dark:text-slate-300">
                  <p><span className="font-semibold text-slate-950 dark:text-white">Payer name:</span> {request.payerName || "-"}</p>
                  <p><span className="font-semibold text-slate-950 dark:text-white">Transfer reference:</span> {request.paymentReference || "-"}</p>
                  <p><span className="font-semibold text-slate-950 dark:text-white">Payment proof:</span> {request.paymentProof ? <a href={request.paymentProof.url} target="_blank" rel="noreferrer" className="text-sky-700 underline dark:text-sky-300">{request.paymentProof.name || "Open proof"}</a> : "-"}</p>
                  <p><span className="font-semibold text-slate-950 dark:text-white">Reviewed at:</span> {formatDate(request.reviewedAt)}</p>
                  {request.adminNote ? <p><span className="font-semibold text-slate-950 dark:text-white">Admin note:</span> {request.adminNote}</p> : null}
                </div>
              ) : null}
            </div>
          </SurfaceCard>

          <SurfaceCard>
            <SectionHeader eyebrow="What unlocks" title="Product depth by tier" description="The upgrade path now matches the real-world approval flow: payment first, entitlement second." />
            <div className="mt-6 space-y-4">
              <div className="rounded-3xl border border-slate-200 bg-slate-50 p-4 dark:border-gray-700 dark:bg-gray-900">
                <div className="flex items-center gap-3">
                  <Sparkles className="h-5 w-5 text-sky-600 dark:text-sky-300" />
                  <div>
                    <p className="text-sm font-semibold text-slate-950 dark:text-white">Pro</p>
                    <p className="mt-1 text-sm leading-7 text-slate-600 dark:text-slate-300">Analytics, exports, journal workflows, messaging, and more structured review loops.</p>
                  </div>
                </div>
              </div>
              <div className="rounded-3xl border border-slate-200 bg-slate-50 p-4 dark:border-gray-700 dark:bg-gray-900">
                <div className="flex items-center gap-3">
                  <Crown className="h-5 w-5 text-amber-600 dark:text-amber-300" />
                  <div>
                    <p className="text-sm font-semibold text-slate-950 dark:text-white">Enterprise</p>
                    <p className="mt-1 text-sm leading-7 text-slate-600 dark:text-slate-300">Shared teams, higher coordination, and desk-level visibility for collaborative environments.</p>
                  </div>
                </div>
              </div>
            </div>
          </SurfaceCard>
        </div>
      </div>
    </div>
  );
}